import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { AUDIENCES, CONTACT_INFO } from "../data/content";
import { Mail, Check, Phone, MapPin } from "lucide-react";
import Seo from "../components/Seo";

export default function Contact() {
  const loc = useLocation();
  const [form, setForm] = useState({
    name: "",
    email: "",
    organisation: "",
    audience: loc?.state?.audience || "",
    message: loc?.state?.subject ? `Enquiry: ${loc.state.subject}\n\n` : "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loc?.state?.audience) setForm((f) => ({ ...f, audience: loc.state.audience }));
  }, [loc?.state?.audience]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSubmitted(true);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <Seo
        title="Contact William Martin — SeedKeepers, Cairns"
        description="Speak to William Martin about a community seed bank, a seed production area, or a restoration-readiness program. Cairns, Australia."
        path="/contact"
      />
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="section-num mb-4">Contact — Seed What Happens</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[0.98]">
              Tell us about <em className="text-eucalypt">your Country</em>, your project, your question.
            </h1>
            <p className="mt-8 text-base md:text-lg text-ink/85 leading-relaxed">
              Let's discuss how a community seed bank, a seed production area
              or a restoration-readiness program could support food security,
              food sovereignty and ecological restoration in your region.
            </p>
            <div className="mt-10 space-y-4">
              <div>
                <p className="specimen-label">Founder</p>
                <p className="font-serif text-2xl mt-1">{CONTACT_INFO.founder}</p>
                <p className="text-xs text-muted">Supporting seed banks since {CONTACT_INFO.founderSince}</p>
              </div>
              <a href={`mailto:${CONTACT_INFO.email}`} className="inline-flex items-center gap-3 link-mono text-eucalypt" data-testid="contact-email-link">
                <Mail size={14} /> {CONTACT_INFO.email}
              </a>
              <a href={`tel:${CONTACT_INFO.phone.replace(/\s+/g, "")}`} className="inline-flex items-center gap-3 link-mono text-eucalypt" data-testid="contact-phone-link">
                <Phone size={14} /> {CONTACT_INFO.phone}
              </a>
              <p className="inline-flex items-center gap-3 specimen-label">
                <MapPin size={14} /> {CONTACT_INFO.location}
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <div className="border border-eucalypt p-10 md:p-12 bg-card" data-testid="contact-success">
                <Check className="text-eucalypt mb-6" size={32} />
                <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-4">
                  Thank you. Your message is filed.
                </h3>
                <p className="text-sm text-muted leading-relaxed mb-6">
                  We'll read it carefully and come back to you within a week.
                </p>
                <button
                  onClick={() => { setSubmitted(false); setForm({ name: "", email: "", organisation: "", audience: "", message: "" }); }}
                  className="link-mono text-eucalypt"
                  data-testid="contact-send-another"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="border border-rule p-8 md:p-12 bg-card space-y-7" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                  <div>
                    <label className="specimen-label">Full name</label>
                    <input required name="name" value={form.name} onChange={onChange} className="field-input" placeholder="e.g. Sarah Ngarrindjeri" data-testid="contact-name" />
                  </div>
                  <div>
                    <label className="specimen-label">Email</label>
                    <input required type="email" name="email" value={form.email} onChange={onChange} className="field-input" placeholder="you@example.com" data-testid="contact-email" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                  <div>
                    <label className="specimen-label">Organisation</label>
                    <input name="organisation" value={form.organisation} onChange={onChange} className="field-input" placeholder="Optional" data-testid="contact-organisation" />
                  </div>
                  <div>
                    <label className="specimen-label">I am a…</label>
                    <select name="audience" value={form.audience} onChange={onChange} className="field-input bg-transparent" data-testid="contact-audience">
                      <option value="">Select pathway</option>
                      {AUDIENCES.map((a) => (
                        <option key={a.slug} value={a.label}>{a.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="specimen-label">Message</label>
                  <textarea
                    required
                    rows={6}
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    className="field-input resize-y leading-relaxed"
                    placeholder="What are you working on? What would be useful to discuss?"
                    data-testid="contact-message"
                  />
                </div>

                {error && (
                  <p className="text-terracotta text-sm font-mono" data-testid="contact-error">{error}</p>
                )}

                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center pt-4">
                  <button
                    type="submit"
                    disabled={busy}
                    className="bg-eucalypt text-chalk px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-eucalypt-dark rounded-full hover:-translate-y-0.5 transition-all disabled:opacity-60"
                    data-testid="contact-submit"
                  >
                    {busy ? "Sending…" : "Send Message"}
                  </button>
                  <a href={`mailto:${CONTACT_INFO.email}`} className="link-mono text-eucalypt" data-testid="contact-mailto">
                    Or email us directly
                  </a>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
